import { useCallback, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { toast } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { useSelectedFeedbackViewFromParam } from 'screens/Levelup/hooks/useSelectedFeedbackViewFromParam';

import { Ellipsis, OutlineEye, Trash } from 'assets/assetsV1/iconComponents';

import { pushFeedbackParams } from 'constants/helper';
import { sendRespondedToFeedbackRequest } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { deleteReceivedFeedbackData } from 'services/continuousFeedback';

import { Menu } from 'uiToolkitV1/Menu';

import { openFeedbackSidePanel } from '../helpers';

const formData = {
  SHARED: 'SHOW_PROVIDED',
  RECEIVED: 'SHOW_RECEIVED',
  REQUESTED: 'SHOW_REQUESTED',
};

const KebabMenu = ({ params, type }) => {
  const { selectedFeedbackView } = useSelectedFeedbackViewFromParam();
  const queryClient = useQueryClient();
  const { uuid: feedbackId } = params.value.requestDetails ?? {};
  const { uuid: feedbackId1, value } = params?.data?.comment ?? {};
  const { canDelete } = value ?? {};
  const isPendingType = type === 'PENDING';
  const isSharedType = type === 'SHARED';
  const isRequestedType = type === 'REQUESTED';
  const isReceivedType = type === 'RECEIVED';
  const formType = formData[type];
  const history = useHistory();

  const openSidePanel = useCallback(
    (e) => {
      e.stopPropagation();
      pushFeedbackParams({
        history,
        formType,
        typeMapped: true,
        feedbackId: feedbackId || feedbackId1,
        selectedFeedbackView,
      });
    },
    [feedbackId, feedbackId1, formType, history, selectedFeedbackView]
  );

  const deleteFeedback = async (e) => {
    e.stopPropagation();
    try {
      if (!feedbackId1) {
        console.error('Feedback ID is undefined');
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const res = await deleteReceivedFeedbackData({ feedbackId: feedbackId1 });
      sendRespondedToFeedbackRequest({
        eventProperties: {
          response: 'Delete',
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['CFTable'] });
      await queryClient.invalidateQueries({ queryKey: ['CFTabs'] });
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.mySummary.mySummaryFeedback] });
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.ReviewPeriodSummaryFeedback] });
      await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.feedbackTable] });

      toast.success('Feedback deleted successfully');
    } catch (err) {
      console.error('Error deleting the feedback: ', err);
      toast.error('Error deleting the feedback');
    }
  };

  const menuOptions = useMemo(
    () => [
      {
        label: 'View Request Details',
        onClick: () => openFeedbackSidePanel({ feedbackId, history }),
        Icon: OutlineEye,
        enabled: isPendingType,
      },
      {
        label: 'View Full Comment',
        onClick: openSidePanel,
        Icon: OutlineEye,
        enabled: isReceivedType || isSharedType || isRequestedType,
      },
      {
        label: 'Delete',
        onClick: deleteFeedback,
        Icon: Trash,
        enabled: canDelete,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      feedbackId,
      history,
      isPendingType,
      isReceivedType,
      isRequestedType,
      isSharedType,
      openSidePanel,
      deleteFeedback,
      canDelete,
      feedbackId1,
    ]
  );

  const validMenuOptions = useMemo(() => menuOptions.filter((option) => option.enabled), [menuOptions]);

  return (
    <Box pt={4}>
      <Menu MenuIcon={Ellipsis} menuList={validMenuOptions} />
    </Box>
  );
};

export default KebabMenu;
